/**
 * Copyright (C) 2023 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

import { StyledEngineProvider } from '@mui/material/styles';
import { size } from 'lodash';
import find from 'lodash/find';
import isNil from 'lodash/isNil';
import React, { useCallback } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { ThemeProvider } from 'styled-components';
import FlightLeg from '../../../shared/entities/flightLeg';
import Terminal, { InstallationType, SteerableBeamPriority } from '../../../shared/entities/terminal';
import {
  selectAppConfig,
  selectFlightLegServiceAlerts,
  selectFlightLegs,
  selectHiddenTerminalsInYourBeamDatapathUuids,
  selectIsFullTailViewEnabled,
  selectIsRequestingFlightLegServiceAlerts,
  selectOverlaidFlightLegHidden,
  selectRouterPathname,
  selectSelectedOverlaidFlightLegId,
  selectSelectedTerminalTabIndex,
  selectTailTagsShown,
  selectTerminalDetails,
  selectTerminalTagSize,
  selectTerminalsInBeam,
} from '../../../store';
import { switchOverlaysContext } from '../../../store/actions';
import { FTV_MAP_ID, addBeamToMap } from '../../../store/maps/actions';
import { OverlaysContextsEnum, TailTagSize } from '../../../store/maps/reducer';
import { setHoveredBeam, setSelectedTerminalTabIndex } from '../../../store/ui/actions';
import { MINUTES_IN_HOUR, durationToNowInMin } from '../../../utils/DateTimeUtils';
import { FEATURE_FLAGS, isFeatureEnabled } from '../../../utils/FeatureFlagUtils';
import { isPathnameFTVExport } from '../../../utils/NavigatorUtils';
import {
  getSelectedTerminalTypeLabelBasedOnTab,
  getTerminalDisplayName,
  isTerminalInBeamPositionInfoBlankedAndVisibilityOnMapDisabled,
} from '../../../utils/TerminalUtilities';
import { FullscreenContext } from '../../common/map/BaseMapContainer';
import appTheme from '../../common/theme/appTheme.json';
import FlightPathContainer from '../FlightPath/FlightPathContainer';
import FlightPathMarkerContainer from '../FlightPath/FlightPathMarkerContainer';
import MptFlightPathContainer from '../MptFlightPath/MptFlightPathContainer';
import MptFlightPathSnackbarContainer from '../MptFlightPath/MptFlightPathSnackbarContainer';
import FullTailViewMapZoomContainer from './FullTailViewMapZoomContainer';

import MptFlightPathWaypointsContainer from '../MptFlightPath/MptFlightPathWaypointsContainer';
import MptServiceAlertsContainer from '../MptFlightPath/MptServiceAlertsContainer';
import TerminalIconMarker from '../TerminalIconMarker';
import OverlayMissionDialogContainer from './OverlayMission/OverlayMissionDialogs/OverlayMissionModalContainer';
import OverlayMissionPlanButtonContainer from './OverlayMission/OverlayMissionPlanButtonContainer';
import {
  getAllFtvTerminals,
  getIfAnyTerminalsOnAircraftUsingSteerableBeam,
  getOtherTerminalsOnAircraft,
} from './utils/fullTerminalViewUtils';
import { SteerableBeamsRing } from '../../../shared/entities/steerableBeam';

interface FullTailViewMapContainerProps {
  map: google.maps.Map;
}

/**
 * This class represents the components that will be rendered on the map in the full tail view. It will include
 * the tail that is currently shown in the FTV, the beam and next beam of that tail, and any terminals that
 * are using the current beam.
 */
function FullTailViewMapContainer(props: FullTailViewMapContainerProps) {
  const { map } = props;
  const dispatch = useDispatch();
  const terminalsInBeam = useSelector(selectTerminalsInBeam, shallowEqual);
  const flightLegs: { [id: number]: FlightLeg } = useSelector(selectFlightLegs, shallowEqual);
  const flightLegServiceAlerts = useSelector(selectFlightLegServiceAlerts, shallowEqual);
  const overlaidFlightLegId = useSelector(selectSelectedOverlaidFlightLegId(FTV_MAP_ID), shallowEqual);
  const isRequestingFlightLegServiceAlerts = useSelector(selectIsRequestingFlightLegServiceAlerts, shallowEqual);
  const flightLegHidden = useSelector(selectOverlaidFlightLegHidden(FTV_MAP_ID), shallowEqual);
  const fullTailViewTerminal = useSelector(selectTerminalDetails, shallowEqual);
  const hiddenTerminalsInYourBeamDatapathUuids = useSelector(selectHiddenTerminalsInYourBeamDatapathUuids, shallowEqual);
  const tailTagsShown = useSelector(selectTailTagsShown(FTV_MAP_ID), shallowEqual);
  const terminalTagSize = useSelector(selectTerminalTagSize(FTV_MAP_ID), shallowEqual);
  const isFullTailViewEnabled = useSelector(selectIsFullTailViewEnabled, shallowEqual);
  const appConfig = useSelector(selectAppConfig, shallowEqual);
  const routerPathname = useSelector(selectRouterPathname, shallowEqual);
  const selectedTerminalTabIndex = useSelector(selectSelectedTerminalTabIndex, shallowEqual);

  let terminals = getAllFtvTerminals(fullTailViewTerminal);

  const selectTerminal = (datapathUuid: string, terminalTabIndex: number, isDoubleClick: boolean = false) => {
    if (isDoubleClick) {
      dispatch(setSelectedTerminalTabIndex(terminalTabIndex));
    }
  };

  const altitudeUnit = 'ft';
  const renderedDatapathUuids = {};
  // Check if the current route is FTV export
  const pathname = routerPathname.substr(1, routerPathname.length);
  const isExport = isPathnameFTVExport(pathname);
  const isDualTerminal = (fullTailViewTerminal || {}).installationType === InstallationType.DUAL_TERMINAL;
  const selectedTerminalType = getSelectedTerminalTypeLabelBasedOnTab(selectedTerminalTabIndex, fullTailViewTerminal);
  const terminalTypeLabel = (fullTailViewTerminal || {}).terminalTypeLabel;
  const beam = !isNil(fullTailViewTerminal?.steerableBeamsSummary)
    ? fullTailViewTerminal!.steerableBeamsSummary.beams.find(
        (beam: SteerableBeamsRing) => beam.beamName === fullTailViewTerminal!.beamName,
      )
    : null;
  const hcxShortestDistanceToBeamEdge =
    !isNil(beam) && !isNil(beam.terminalShortestDistanceToBeamEdgeMapping)
      ? beam.terminalShortestDistanceToBeamEdgeMapping[fullTailViewTerminal!.datapathUuid]
      : null;

  const addBeamToMapHandler = useCallback(
    (name: string, networkType: string, mapType: string) => {
      dispatch(switchOverlaysContext(OverlaysContextsEnum.FTV));
      dispatch(addBeamToMap(FTV_MAP_ID)(name, networkType, mapType));
    },
    [dispatch],
  );

  const setHoveredBeamHandler = useCallback(
    (beamName: string) => {
      dispatch(setHoveredBeam(beamName));
    },
    [dispatch],
  );

  return (
    <React.Fragment>
      {fullTailViewTerminal && !isNil(fullTailViewTerminal.latitudeDeg) && !isNil(fullTailViewTerminal.longitudeDeg) ? (
        <React.Fragment>
          <StyledEngineProvider injectFirst>
            <ThemeProvider theme={appTheme}>
              <React.Fragment>
                <FlightPathContainer map={map} />
                {!isNil(overlaidFlightLegId) && !isNil(flightLegServiceAlerts) && (
                  <>
                    <MptFlightPathWaypointsContainer
                      map={map}
                      flightLeg={flightLegs[overlaidFlightLegId]}
                      flightLegServiceAlerts={flightLegServiceAlerts}
                      overlaidFlightLegHidden={flightLegHidden}
                    />
                    <MptFlightPathContainer
                      map={map}
                      flightLeg={flightLegs[overlaidFlightLegId]}
                      flightLegServiceAlerts={flightLegServiceAlerts}
                      overlaidFlightLegHidden={flightLegHidden}
                      addBeamToMap={addBeamToMapHandler}
                    />
                    <MptServiceAlertsContainer
                      map={map}
                      flightLeg={flightLegs[overlaidFlightLegId]}
                      flightLegServiceAlerts={flightLegServiceAlerts}
                      isRequestingFlightLegServiceAlerts={isRequestingFlightLegServiceAlerts}
                      overlaidFlightLegHidden={flightLegHidden}
                      setHoveredBeam={setHoveredBeamHandler}
                      mapId={FTV_MAP_ID}
                    />
                  </>
                )}
                <FlightPathMarkerContainer map={map} />
              </React.Fragment>
            </ThemeProvider>
          </StyledEngineProvider>
          <TerminalIconMarker
            key={fullTailViewTerminal.datapathUuid}
            datapathUuid={fullTailViewTerminal.datapathUuid}
            map={map as unknown as google.maps.Map}
            lat={fullTailViewTerminal.latitudeDeg}
            lng={fullTailViewTerminal.longitudeDeg}
            connectivityStatus={fullTailViewTerminal.connectivityStatus}
            headingDeg={fullTailViewTerminal.headingDeg}
            aircraftTailNumber={getTerminalDisplayName(fullTailViewTerminal)}
            terminalTypeLabel={fullTailViewTerminal.terminalTypeLabel}
            networkGroup={fullTailViewTerminal.networkGroup}
            installationType={fullTailViewTerminal.installationType}
            networkOrigin={fullTailViewTerminal.networkOrigin}
            beamDisplayName={fullTailViewTerminal.beamDisplayName}
            flDataRateBps={fullTailViewTerminal.flDataRateBps}
            rlDataRateBps={fullTailViewTerminal.rlDataRateBps}
            flightPhase={fullTailViewTerminal.flightPhase}
            flSignalQuality={fullTailViewTerminal.flSignalQuality}
            altitudeFeet={fullTailViewTerminal.altitudeFeet}
            altitudeUnit={altitudeUnit}
            platformType={fullTailViewTerminal.platformType}
            selected={true}
            selectTerminal={selectTerminal}
            selectedTerminalTabIndex={selectedTerminalTabIndex}
            otherTerminalsOnAircraft={getOtherTerminalsOnAircraft(fullTailViewTerminal.datapathUuid, terminals)}
            otherTerminalOnAircraftSelectedId={null}
            isTailTagTransparent={true}
            sourceTime={fullTailViewTerminal.sourceTime}
            isTailTagShown={tailTagsShown}
            isFullTailViewEnabled={isFullTailViewEnabled}
            isPrimaryTerminal={fullTailViewTerminal.steerableBeamPriority === SteerableBeamPriority.PRIMARY}
            isSecondaryTerminal={fullTailViewTerminal.steerableBeamPriority === SteerableBeamPriority.SECONDARY}
            hcxShortestDistanceToBeamEdge={hcxShortestDistanceToBeamEdge}
            areAnyTerminalsOnAircraftUsingSteerableBeam={getIfAnyTerminalsOnAircraftUsingSteerableBeam(
              fullTailViewTerminal,
              fullTailViewTerminal.terminalsOnAircraft,
            )}
            disableHighestPriorityStatus={isDualTerminal}
            disableAntennaTags={terminalTagSize === TailTagSize.SMALL}
            useOtherTerminalConnStatus={isDualTerminal && selectedTerminalType !== terminalTypeLabel}
            lwSiteId={fullTailViewTerminal.lwSiteId}
            lwTxPowerDb={fullTailViewTerminal.lwTxPowerDb}
          />
        </React.Fragment>
      ) : null}
      <React.Fragment>
        {fullTailViewTerminal &&
          isFeatureEnabled(appConfig, FEATURE_FLAGS.isTerminalInBeamEnabled) &&
          size(terminalsInBeam) &&
          terminalsInBeam
            // Remove hidden terminals, terminals that have been offline more than an hour, blanked / disabled terminals
            .filter(
              (terminalInBeam: Terminal) =>
                terminalInBeam &&
                !find(
                  fullTailViewTerminal.terminalsOnAircraft,
                  (terminalOnAircraft: Terminal) => terminalOnAircraft.datapathUuid === terminalInBeam.datapathUuid,
                ) &&
                !isNil(terminalInBeam.latitudeDeg) &&
                !isNil(terminalInBeam.longitudeDeg) &&
                !hiddenTerminalsInYourBeamDatapathUuids.includes(terminalInBeam.datapathUuid) &&
                !isTerminalInBeamPositionInfoBlankedAndVisibilityOnMapDisabled(terminalInBeam) &&
                durationToNowInMin(terminalInBeam.sourceTime) <= MINUTES_IN_HOUR,
            )
            .map((terminalInBeam: Terminal) => {
              // If the terminal has already been rendered, do not render it again (prevents multi-terminal aircraft in beam from being rendered twice)
              // TODO Refactor this into a common method along with the logic on the Fleet View Map Container. https://jira.viasat.com/browse/UP-1499
              const isDualTerminal = terminalInBeam.installationType === InstallationType.DUAL_TERMINAL;

              const beam = !isNil(terminalInBeam?.steerableBeamsSummary)
                ? terminalInBeam!.steerableBeamsSummary.beams.find(
                    (beam: SteerableBeamsRing) => beam.beamName === terminalInBeam!.beamName,
                  )
                : null;
              const hcxShortestDistanceToBeamEdge =
                !isNil(beam) && !isNil(beam.terminalShortestDistanceToBeamEdgeMapping)
                  ? beam.terminalShortestDistanceToBeamEdgeMapping[terminalInBeam!.datapathUuid]
                  : null;

              const terminalToRender = renderedDatapathUuids[terminalInBeam.datapathUuid] ? null : (
                <React.Fragment key={terminalInBeam.datapathUuid}>
                  <TerminalIconMarker
                    key={terminalInBeam.datapathUuid}
                    datapathUuid={terminalInBeam.datapathUuid}
                    map={map as unknown as google.maps.Map}
                    lat={terminalInBeam.latitudeDeg}
                    lng={terminalInBeam.longitudeDeg}
                    connectivityStatus={terminalInBeam.connectivityStatus}
                    headingDeg={terminalInBeam.headingDeg}
                    aircraftTailNumber={getTerminalDisplayName(terminalInBeam)}
                    terminalTypeLabel={terminalInBeam.terminalTypeLabel}
                    networkGroup={fullTailViewTerminal.networkGroup}
                    networkOrigin={terminalInBeam.networkOrigin}
                    beamDisplayName={terminalInBeam.beamDisplayName}
                    flDataRateBps={terminalInBeam.flDataRateBps}
                    rlDataRateBps={terminalInBeam.rlDataRateBps}
                    flightPhase={terminalInBeam.flightPhase}
                    flSignalQuality={fullTailViewTerminal.flSignalQuality}
                    altitudeFeet={terminalInBeam.altitudeFeet}
                    altitudeUnit={altitudeUnit}
                    platformType={terminalInBeam.platformType}
                    selected={false}
                    selectTerminal={selectTerminal}
                    otherTerminalsOnAircraft={getOtherTerminalsOnAircraft(terminalInBeam.datapathUuid, terminals)}
                    otherTerminalOnAircraftSelectedId={null}
                    isTailTagTransparent={true}
                    sourceTime={terminalInBeam.sourceTime}
                    isTailTagShown={tailTagsShown}
                    isFullTailViewEnabled={appConfig?.isFullTailViewEnabled}
                    isPrimaryTerminal={terminalInBeam.steerableBeamPriority === SteerableBeamPriority.PRIMARY}
                    isSecondaryTerminal={terminalInBeam.steerableBeamPriority === SteerableBeamPriority.SECONDARY}
                    useGreatestSteerableBeamPriorityIconOfAllTerminalsOnAircraft
                    hcxShortestDistanceToBeamEdge={hcxShortestDistanceToBeamEdge}
                    areAnyTerminalsOnAircraftUsingSteerableBeam={getIfAnyTerminalsOnAircraftUsingSteerableBeam(
                      terminalInBeam,
                      getOtherTerminalsOnAircraft(terminalInBeam.datapathUuid, terminals),
                    )}
                    disableHighestPriorityStatus={isDualTerminal}
                    disableAntennaTags={terminalTagSize === TailTagSize.SMALL}
                    lwSiteId={terminalInBeam.lwSiteId}
                    lwTxPowerDb={terminalInBeam.lwTxPowerDb}
                  />
                </React.Fragment>
              );

              // Accumulate the ids of rendered terminals and the ids of the datapathUuidsOnAircaft for multi-terminal aircraft
              renderedDatapathUuids[terminalInBeam.datapathUuid] = true;
              if (terminalInBeam.datapathUuidsOnAircraft) {
                terminalInBeam.datapathUuidsOnAircraft.forEach((datapathUuid: string) => {
                  renderedDatapathUuids[datapathUuid] = true;
                });
              }

              // Render the terminal on the map
              return terminalToRender;
            })}
      </React.Fragment>
      <OverlayMissionDialogContainer />
      <FullscreenContext.Consumer>
        {isFullscreen => {
          return isFullscreen || isExport ? null : <OverlayMissionPlanButtonContainer />;
        }}
      </FullscreenContext.Consumer>
      <MptFlightPathSnackbarContainer />

      <FullTailViewMapZoomContainer map={map} />
    </React.Fragment>
  );
}

export default FullTailViewMapContainer;
